"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { isMcpEnabledForTier } from "@/lib/mcp-flag-tier";
import type { DbModelTier } from "@/types/db";

const McpFlagContext = createContext(false);

export function McpFlagProvider({
  allowedModelTiers,
  children,
}: {
  allowedModelTiers: DbModelTier[] | null;
  children: ReactNode;
}) {
  const [userModelTier, setUserModelTier] = useState<DbModelTier>("standard");

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      const { data } = await supabase.from("profiles").select("model_tier").eq("user_id", user.id).single();
      if (!cancelled && data) setUserModelTier(data.model_tier ?? "standard");
    })();

    return () => { cancelled = true; };
  }, []);

  const mcpEnabled = useMemo(
    () => isMcpEnabledForTier(allowedModelTiers === null ? null : { model_tiers: allowedModelTiers }, userModelTier),
    [allowedModelTiers, userModelTier]
  );

  return <McpFlagContext.Provider value={mcpEnabled}>{children}</McpFlagContext.Provider>;
}

export function useMcpFlag() {
  return useContext(McpFlagContext);
}
